import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { parseLiveBytes } from './liveBytesParser'

/**
 * LiveSpamButtonControl — PM-2 (Hold-to-Spam, No Acknowledgement)
 *
 * Press and hold → spams the configured byte to hardware at the configured interval (default 100 ms).
 * Release (or pointer leave/cancel) → stops immediately.
 * Never waits for Rdone; never blocks or is blocked by the global gate.
 *
 * Two distinct image states (defaultImage / tapImage) give visual feedback
 * while the button is physically held.
 *
 * Props:
 *   comp           – full component object from play data
 *   outputStates   – current output state map
 *   onChange       – (mapping, value) callback
 *   resolveImage   – image resolver function
 *   highlightClass – CSS class string for step highlighting
 *   style          – positioning style object
 */

const DEFAULT_SPAM_INTERVAL_MS = 100

function LiveSpamButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    onRequestActivate,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const isPressedRef = useRef(false)
    const spamIntervalRef = useRef(null)
    const cleanupRefs = useRef({})

    const liveBytesStr = comp.properties.liveBytes || ''
    const liveBytesArray = useMemo(() => parseLiveBytes(liveBytesStr), [liveBytesStr])
    const spamIntervalMs = Math.max(50, Number(comp.properties.spamIntervalMs ?? DEFAULT_SPAM_INTERVAL_MS))

    const sendSpamBytes = useCallback(() => {
        if (!liveBytesArray.length || !worker || !webSerial?.isConnected) return
        worker.postMessage({ type: 'writeArray', value: liveBytesArray })
    }, [liveBytesArray, worker, webSerial])

    const startSpam = useCallback(() => {
        if (spamIntervalRef.current) return
        sendSpamBytes()
        spamIntervalRef.current = setInterval(sendSpamBytes, spamIntervalMs)
    }, [sendSpamBytes, spamIntervalMs])

    const stopSpam = useCallback(() => {
        if (spamIntervalRef.current) {
            clearInterval(spamIntervalRef.current)
            spamIntervalRef.current = null
        }
    }, [])

    cleanupRefs.current.onChange = onChange
    cleanupRefs.current.stopSpam = stopSpam
    cleanupRefs.current.mapping = comp.mapping

    // Unmount safety: stop spam and reset visual state if still held.
    useEffect(() => {
        return () => {
            cleanupRefs.current.stopSpam()
            if (isPressedRef.current) {
                cleanupRefs.current.onChange(cleanupRefs.current.mapping, 0)
            }
        }
    }, [])

    const handlePointerDown = useCallback(
        (e) => {
            // Always capture pointer and update visual state so release events
            // are tracked and the button feels responsive.
            e.currentTarget.setPointerCapture(e.pointerId)
            isPressedRef.current = true
            onChange(comp.mapping, 1)
            // Only start the spam loop if the PM-4 lock allows it.
            // Connection check is inside sendSpamBytes — bytes silently
            // skipped when disconnected, visual state still shows pressed.
            if (!onRequestActivate || onRequestActivate()) {
                startSpam()
            }
        },
        [comp.mapping, onChange, startSpam, onRequestActivate]
    )

    const handlePointerUp = useCallback(() => {
        isPressedRef.current = false
        stopSpam()
        onChange(comp.mapping, 0)
    }, [comp.mapping, onChange, stopSpam])

    const isBtnOn = (outputStates[comp.mapping] || 0) !== 0
    // Admin names images "Default Image" and "Tap Image" per PM-2 spec.
    // Fall back to onImage/offImage for backward compatibility.
    const btnImg = isBtnOn
        ? comp.properties.tapImage || comp.properties.onImage || comp.properties.imageUrl
        : comp.properties.defaultImage || comp.properties.offImage || comp.properties.imageUrl

    return (
        <div
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onDragStart={(e) => e.preventDefault()}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            <span className="control-label">
                {comp.properties.label || comp.name}
            </span>
        </div>
    )
}

export default LiveSpamButtonControl
